import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Alert,
  Card,
  CardContent,
  Paper,
  Skeleton,
  Stack,
  useTheme,
} from '@mui/material';
import { Alley } from '../../../../services/entities/impl/blfApi/schemas/alley';
import { Scan } from '../../../../services/entities/impl/blfApi/schemas/scan';
import {
  Upload,
  UploadType,
} from '../../../../services/entities/impl/blfApi/schemas/upload';
import { UploadDetail } from '../../../../services/entities/impl/blfApi/schemas/uploadSummary';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { ValidationCounts } from '../../utils/validation/createValidationSummary';
import { getValidationCounts } from '../../utils/validation/getValidationCounts';
import { CollectionFileStatus } from './CollectionFileStatus';
import { CollectionHeader } from './CollectionHeader';
import { CollectionScansOverview } from './CollectionScansOverview';
import { CollectionUploadProgress } from './CollectionUploadProgress';
import { CollectionValidationSummary } from './CollectionValidationSummary';
import { checkScanProcessingStatus } from './utils/checkScanProcessingStatus';
export type CollectionSummaryProps = {
  collectionId?: string;
  uploads?: Upload[];
  alleys?: Alley[];
  scans?: Scan[];
  uploadDetail?: UploadDetail;
  collectionValidationCounts?: ValidationCounts;
  uploadSummaryValidationCounts?: ValidationCounts;
  uploadDetailValidationCounts?: ValidationCounts;
  scansValidationCounts?: ValidationCounts;
  alleysValidationCounts?: ValidationCounts;
  uploadsValidationCounts?: ValidationCounts;
  isLoading?: boolean;
};

export const CollectionSummary = ({
  collectionId,
  uploads = [],
  alleys = [],
  scans = [],
  uploadDetail,
  collectionValidationCounts,
  uploadSummaryValidationCounts,
  uploadDetailValidationCounts,
  scansValidationCounts,
  alleysValidationCounts,
  uploadsValidationCounts,
  isLoading = false,
}: CollectionSummaryProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Collection.json status check
  const collectionJsonUploads = uploads.filter(
    (upload) => upload.type === UploadType.COLLECTION_JSON
  );

  const collectionJsonStatus =
    collectionJsonUploads.length === 0
      ? 'error'
      : collectionJsonUploads.length > 1
      ? 'warning'
      : 'success';

  // Bag upload progress calculation
  const bagUploads = uploads.filter((upload) => upload.type === 'bag');
  const uploadProgress =
    alleys.length > 0
      ? Math.min(100, (bagUploads.length / alleys.length) * 100)
      : 0;

  const hasTooManyBags = bagUploads.length > alleys.length;

  // Determine progress bar color
  const progressStatus = hasTooManyBags
    ? 'warning'
    : uploadProgress === 100
    ? 'success'
    : 'primary';

  // Check for delayed scan processing using the utility function
  const scanProcessingResult = checkScanProcessingStatus(uploadDetail, scans);
  const scanProcessingCounts = getValidationCounts(scanProcessingResult);

  // Check if everything is perfect
  const hasNoIssues =
    (!collectionValidationCounts ||
      (collectionValidationCounts.errors === 0 &&
        collectionValidationCounts.warnings === 0)) &&
    (!uploadSummaryValidationCounts ||
      (uploadSummaryValidationCounts.errors === 0 &&
        uploadSummaryValidationCounts.warnings === 0)) &&
    (!uploadDetailValidationCounts ||
      (uploadDetailValidationCounts.errors === 0 &&
        uploadDetailValidationCounts.warnings === 0)) &&
    (!scansValidationCounts ||
      (scansValidationCounts.errors === 0 &&
        scansValidationCounts.warnings === 0)) &&
    (!alleysValidationCounts ||
      (alleysValidationCounts.errors === 0 &&
        alleysValidationCounts.warnings === 0)) &&
    (!uploadsValidationCounts ||
      (uploadsValidationCounts.errors === 0 &&
        uploadsValidationCounts.warnings === 0)) &&
    collectionJsonStatus === 'success' &&
    !hasTooManyBags &&
    uploadProgress === 100 &&
    scanProcessingResult.valid;

  // Count total issues
  const totalErrors =
    (collectionValidationCounts?.errors || 0) +
    (uploadSummaryValidationCounts?.errors || 0) +
    (uploadDetailValidationCounts?.errors || 0) +
    (scansValidationCounts?.errors || 0) +
    (alleysValidationCounts?.errors || 0) +
    (uploadsValidationCounts?.errors || 0) +
    (collectionJsonStatus === 'error' ? 1 : 0) +
    scanProcessingCounts.errors;

  const totalWarnings =
    (collectionValidationCounts?.warnings || 0) +
    (uploadSummaryValidationCounts?.warnings || 0) +
    (uploadDetailValidationCounts?.warnings || 0) +
    (scansValidationCounts?.warnings || 0) +
    (alleysValidationCounts?.warnings || 0) +
    (uploadsValidationCounts?.warnings || 0) +
    (collectionJsonStatus === 'warning' ? 1 : 0) +
    (hasTooManyBags ? 1 : 0) +
    scanProcessingCounts.warnings;

  if (isLoading) {
    return (
      <Card
        variant='outlined'
        sx={{ mb: 3, bgcolor: theme.vars.palette.background.default }}
      >
        <CardContent sx={{ '&:last-child': { pb: '16px' } }}>
          <Stack spacing={2}>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
            >
              <Skeleton variant='text' width={200} height={32} />
              <Skeleton variant='rounded' width={80} height={24} />
            </Stack>

            <Skeleton variant='text' width={300} height={20} />
            <Skeleton variant='rounded' height={56} />

            <Paper variant='outlined' sx={{ p: 1.5, borderRadius: 1 }}>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
              >
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Skeleton variant='circular' width={24} height={24} />
                  <Skeleton variant='text' width={120} />
                </Stack>
                <Skeleton variant='rounded' width={100} height={24} />
              </Stack>
            </Paper>

            <Paper variant='outlined' sx={{ p: 1.5, borderRadius: 1 }}>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                sx={{ mb: 1 }}
              >
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Skeleton variant='circular' width={24} height={24} />
                  <Skeleton variant='text' width={120} />
                </Stack>
                <Skeleton variant='text' width={80} />
              </Stack>
              <Skeleton variant='rounded' height={8} sx={{ mb: 1 }} />
            </Paper>

            <Paper variant='outlined' sx={{ p: 1.5, borderRadius: 1 }}>
              <Stack spacing={1}>
                <Skeleton variant='text' width={150} height={28} />
                <Skeleton variant='rectangular' height={120} />
              </Stack>
            </Paper>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      variant='outlined'
      sx={{ mb: 3, bgcolor: theme.vars.palette.background.default }}
    >
      <CardContent sx={{ '&:last-child': { pb: '16px' } }}>
        <Stack spacing={2}>
          {/* Header with overall status */}
          <CollectionHeader
            collectionId={collectionId}
            hasNoIssues={hasNoIssues}
            totalErrors={totalErrors}
            totalWarnings={totalWarnings}
          />

          {/* Perfect status message */}
          {hasNoIssues && (
            <Alert
              icon={<CheckCircleOutlineIcon />}
              severity='success'
              sx={{ mb: 1 }}
            >
              {t('All checks passed!')}
            </Alert>
          )}

          {/* Collection.json Status */}
          <CollectionFileStatus
            status={collectionJsonStatus as 'success' | 'error' | 'warning'}
            uploadCount={collectionJsonUploads.length}
          />

          {/* Upload Progress */}
          <CollectionUploadProgress
            bagUploadsCount={bagUploads.length}
            alleysCount={alleys.length}
            uploadProgress={uploadProgress}
            progressStatus={progressStatus}
          />

          {/* Scan Status */}
          <CollectionScansOverview scans={scans} uploadDetail={uploadDetail} />

          {/* Validation Summary */}
          <CollectionValidationSummary
            collectionValidationCounts={collectionValidationCounts}
            uploadSummaryValidationCounts={uploadSummaryValidationCounts}
            uploadDetailValidationCounts={uploadDetailValidationCounts}
            scansValidationCounts={scansValidationCounts}
            alleysValidationCounts={alleysValidationCounts}
            uploadsValidationCounts={uploadsValidationCounts}
            totalErrors={totalErrors}
            totalWarnings={totalWarnings}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};
